import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import Spinner from "./Spinner";
import PoweredByFooter from "./PoweredByFooter";
import AuthScreenHeader from "./AuthScreenHeader";

const MIN_PASSWORD_LENGTH = 8;

// The set-a-new-password flow both reset links land on — volunteer
// (/:parish/reset-password) and staff (/reset-password). The recovery
// -session detection below is timing-sensitive, so it lives once here
// and the two views stay thin wrappers that pick brand, copy, and
// where "back to sign in" goes.
interface Props {
  // Parish brand mark for the volunteer surface; staff surface omits
  // it and gets AuthScreenHeader's default product mark.
  brand?: ReactNode;
  // Where "back to sign in" / "go to sign in" navigate.
  signInPath: string;
  // Confirmation body shown once the password is updated.
  doneMessage: string;
  // Volunteer surfaces carry the "Powered by" footer; the staff one
  // doesn't (matching SignIn.tsx).
  showFooter?: boolean;
}

// "checking": waiting to see whether the reset link established a
// session. "ready": it did — show the new-password form. "invalid":
// nothing showed up in time — the link was missing, already used, or
// expired.
type RecoveryState = "checking" | "ready" | "invalid";
type SubmitStatus = "idle" | "saving" | "error" | "done";

export default function ResetPasswordFlow({
  brand,
  signInPath,
  doneMessage,
  showFooter = false,
}: Props) {
  const navigate = useNavigate();
  const [recovery, setRecovery] = useState<RecoveryState>("checking");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [status, setStatus] = useState<SubmitStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!supabase) return;
    let cancelled = false;

    const markReady = () => {
      if (!cancelled) setRecovery("ready");
    };

    // supabase-js parses the reset link's token out of the URL and
    // establishes a session automatically (detectSessionInUrl,
    // default on) — but that can happen either synchronously before
    // this effect even subscribes, or a moment later via an async
    // token exchange. Checking getSession() directly on mount catches
    // the "already happened" case; the onAuthStateChange subscription
    // catches the "happens shortly after" case. Together they cover
    // both timings without depending on catching one specific event
    // at one specific instant.
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) markReady();
    });

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY" || session) markReady();
    });

    // Nothing showed up — the link is missing its token, was already
    // used, or expired. Stop "Checking…" from spinning forever.
    const timer = setTimeout(() => {
      setRecovery((r) => (r === "checking" ? "invalid" : r));
    }, 3000);

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
      clearTimeout(timer);
    };
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!supabase) return;
    if (password.length < MIN_PASSWORD_LENGTH) {
      setStatus("error");
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setStatus("error");
      setError("Passwords don't match.");
      return;
    }
    setStatus("saving");
    setError(null);
    const { error: updateError } = await supabase.auth.updateUser({ password });
    if (updateError) {
      setStatus("error");
      setError(updateError.message);
      return;
    }
    // The recovery link's session is a real (if narrowly-scoped)
    // session — sign it out so "log in with your new password" is a
    // genuine fresh login, not a no-op against an already-open one.
    await supabase.auth.signOut();
    setStatus("done");
  }

  const footer = showFooter ? <PoweredByFooter /> : null;

  if (recovery === "checking") {
    return (
      <main className="page page--sign-in">
        <div className="sign-in">
          <AuthScreenHeader brand={brand} title="Reset your password" />
          <Spinner label="Checking your reset link" />
        </div>
      </main>
    );
  }

  if (recovery === "invalid") {
    return (
      <main className="page page--sign-in">
        <div className="sign-in">
          <AuthScreenHeader
            brand={brand}
            title="Link expired"
            subtitle="This password reset link is invalid or has expired. Request a new one from the sign-in page."
          />
          <button type="button" className="btn" onClick={() => navigate(signInPath)}>
            Back to sign in
          </button>
        </div>
        {footer}
      </main>
    );
  }

  if (status === "done") {
    return (
      <main className="page page--sign-in">
        <div className="sign-in">
          <AuthScreenHeader brand={brand} title="Password updated" />
          <div className="sign-in__confirmation">
            <p>{doneMessage}</p>
          </div>
          <button type="button" className="btn" onClick={() => navigate(signInPath)}>
            Go to sign in
          </button>
        </div>
        {footer}
      </main>
    );
  }

  return (
    <main className="page page--sign-in">
      <div className="sign-in">
        <AuthScreenHeader
          brand={brand}
          title="Set a new password"
          subtitle="Choose a new password for your account."
        />

        <form className="sign-in__form" onSubmit={submit}>
          <label className="sign-in__field">
            <span className="sign-in__label">New password</span>
            <input
              type="password"
              className="sign-in__input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              minLength={MIN_PASSWORD_LENGTH}
              required
              autoFocus
            />
          </label>
          <label className="sign-in__field">
            <span className="sign-in__label">Confirm password</span>
            <input
              type="password"
              className="sign-in__input"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              minLength={MIN_PASSWORD_LENGTH}
              required
            />
          </label>
          <button type="submit" className="btn" disabled={status === "saving"}>
            {status === "saving" ? "Saving…" : "Set new password"}
          </button>
          {error ? <p className="sign-in__error">{error}</p> : null}
        </form>
      </div>
      {footer}
    </main>
  );
}
